import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Col, Container, Dropdown, Form, Modal, Row } from "react-bootstrap";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import {
  type Case,
  type Client,
  type ContentGetMiscContentFilteredRequest,
  type CreateDiscoveryRequest,
  type DocumentDetail,
  DocumentSubType,
  DocumentType,
  ExternalPartners,
  MiscContentType,
  type Party,
  PartyPosition,
  type Question,
  Representative,
  Role,
  ToggleableFeature,
} from "@/briefpoint-client";
import Button from "@/components/Button";
import Help from "@/components/Help";
import { Modal as BPModal } from "@/components/Modals";
import {
  useCaseApi,
  useClientApi,
  useContentApi,
  useDocumentApi,
  useFirmApi,
  useTagApi,
} from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import useDocuments from "@/hooks/useDocuments";
import type {
  CaseResponse,
  ClientResponse,
  DocumentClient,
  VenueResponse,
} from "@/lib/types";
import {
  addUploadDate,
  getCaseJurisdiction,
  getSubTypeText,
  hasCanceledSubscription,
  isInTrialOrHasActiveSubscription,
  leadAttorneyName,
  sanitizeFiles,
  userCanUseFeature,
} from "@/lib/utils";
import "./styles.scss";
import "./case.scss";
import rg4js from "raygun4js";
import CaseDiscovery from "@/components/CaseDiscovery";
import AddEditCaseModal from "@/components/CaseManagement/AddEditCaseModal";
import CaseProduction from "@/components/CaseProduction";
import CaseTabs from "@/components/CaseTabs/CaseTabs";
import GeneralInformationScreen from "@/components/ConfirmInfo/GeneralInformation";
import ExternalFileBrowserSelect from "@/components/ExternalPartner/ExternalFileBrowserSelect";
import ExternalPartnerConnection from "@/components/ExternalPartner/ExternalPartnerConnection";
import ExternalPartnerMenu from "@/components/ExternalPartner/ExternalPartnerMenu";
import LeavingGuard from "@/components/LeavingGuard";
import Loading from "@/components/Loading";
import SignUpModal from "@/components/Modals/SignUpModal";
import { TagsList } from "@/components/TagsList";
import { toast } from "@/components/ui/toast";
import useFirm from "@/hooks/useFirm";
import ClioLogo from "@/images/Glyph_Clio Glyph Blue.svg?react";
import BriefpointLogo from "@/images/logo.svg?react";
import MyCaseLogo from "@/images/MyCase.svg?react";
import SmokeballLogo from "@/images/Smokeball_logo_mark.svg?react";
import { CaliforniaId, CASE_TABS, PARTY_SPECIFIC_TAGS } from "@/lib/constants";
import {
  caseGetOptions,
  getClientsOptions,
  getFirmCasesOptions,
  getProcessingDocumentsOptions,
  getUnarchivedCaseDocumentListOptions,
} from "@/lib/query-options";
import type { CaseTab } from "@/lib/types";
import {
  caseTypeDisplay,
  getUploadingDocument,
  toLocaleUSDateString,
} from "@/lib/utils";
import { convertPartyPositionToPropoundingParty } from "@/screens/DocumentGeneration/GenerateDiscoveryRequestPage";
import RequiredCaseInfo from "@/screens/DocumentGeneration/RequiredCaseInfo";
import SelectPartyModal from "@/screens/DocumentGeneration/SelectPartyModal";
import { filterTagsForParties } from "@/utils/Requests/utils";

type CaseParams = {
  id: string;
};

export default function CaseOverviewPage() {
  const { user, firm } = useAuth();
  if (!user) throw new Error("User is undefined");
  if (!firm) throw new Error("Firm is undefined");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const { id } = useParams<CaseParams>();
  const caseApi = useCaseApi();
  const clientApi = useClientApi();
  const contentApi = useContentApi();
  const documentApi = useDocumentApi();
  const [users, , , ,] = useFirm(firm?.id, false);
  const [uploadingDocuments, setUploadingDocuments] = useState<
    DocumentClient[]
  >([]);
  const [isSignUpModalShow, setIsSignUpModalShow] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [_, uploadFiles] = useDocuments(true, id);
  const firmApi = useFirmApi();

  // QUERIES
  const caseQueryOptions = caseGetOptions(
    caseApi,
    user?.firmId ?? "",
    id ?? "",
  );

  // Query used to retrieve the current case data
  const caseQuery = useQuery({
    ...caseQueryOptions,
    select: (data) => data as CaseResponse,
    enabled: !!user?.firmId && !!id,
  });

  // Query used to retrieve all cases for the firm
  const casesQuery = useQuery({
    ...getFirmCasesOptions(firmApi, user?.firmId ?? ""),
  });

  // Query used to retrieve all clients for the firm (same options as ClientsTable / AddEditClientModal)
  const clientsQuery = useQuery({
    ...getClientsOptions(clientApi, user),
    select: (data) => data as ClientResponse[],
    enabled: !!user?.firmId,
  });

  // Query used to retrieve all documents for the case
  const documentsQuery = useQuery({
    ...getUnarchivedCaseDocumentListOptions(documentApi, id ?? ""),
    select: (data) => {
      return data.map((document) => addUploadDate(document as DocumentDetail));
    },
  });
  /** Bumps when `caseQuery` cache changes (optimistic update + refetch from `updateCaseMutation`) so RequiredCaseInfo remounts with fresh case fields. */
  const requiredCaseInfoKey = `${id}-${caseQuery.dataUpdatedAt}`;

  const [, setError] = useState("");
  const [showEditCase, setShowEditCase] = useState(false);
  const { jurisdictions } = useAuth();
  const tagApi = useTagApi();
  const history = useHistory();
  const [tags, setTags] = useState<Map<number, string> | undefined>();
  const [typeToGenerate, setTypeToGenerate] = useState<
    DocumentSubType | undefined
  >(
    Number(new URLSearchParams(location.search).get("generate")) as
      | DocumentSubType
      | undefined,
  );
  const [showCloseCase, setShowCloseCase] = useState(false);
  const [showUploadDocs, setShowUploadDocs] = useState(false);
  const [deleteDocuments, setDeleteDocuments] = useState(false);
  const pendingDraftType = useRef<DocumentSubType>(DocumentSubType.Unknown);
  const [isExternalUploading, setIsExternalUploading] =
    useState<boolean>(false);

  const userCanUseDocHandling = userCanUseFeature(
    user,
    ToggleableFeature.Autodoc,
  );
  const selectedTab =
    (new URLSearchParams(location.search).get("tab") as CaseTab | undefined) ??
    CASE_TABS.DISCOVERY;

  const jurisdiction = useMemo(() => {
    if (!jurisdictions || !firm) {
      return undefined;
    }

    const firmJx =
      firm.offices?.find((x) => x.jurisdictions?.length)?.jurisdictions?.[0]
        ?.jurisdictionId ?? CaliforniaId;
    const jx =
      jurisdictions?.find((j) => j.id === caseQuery.data?.jurisdiction) ??
      jurisdictions?.find((j) => j.id === firmJx);
    return jx;
  }, [jurisdictions, firm, caseQuery.data?.jurisdiction]);

  const venue = (caseQuery.data?.venue &&
    jurisdiction?.venues?.find((x) => x.id === caseQuery.data.venue)) as
    | VenueResponse
    | undefined;
  const nonArchivedDocs = documentsQuery.data?.filter((x) => !x.isArchived);
  const partyTags = useRef(new Map<number, string>());
  const hasProvidedInfo =
    user?.firstName &&
    user?.lastName &&
    user?.barNumber &&
    (!user?.role?.includes(Role.FirmAdmin) ||
      (firm?.name && firm?.address?.street && firm?.address.postalCode));

  const missingRequiredInfo = useMemo(() => {
    return !caseQuery.data?.caseType || !caseQuery.data?.clientPosition;
  }, [caseQuery.data]);

  function toDateOrMin(val?: string | null) {
    return val ? new Date(val) : new Date(-8640000000000000);
  }

  useEffect(() => {
    let tagsLoaded = false;
    async function loadTags() {
      const tagsList = new Map<number, string>();
      const loadedTags = await tagApi.tagFindTags({});
      for (const t of loadedTags) {
        tagsList.set(t.id, t.name);
        if (t.ownerId || PARTY_SPECIFIC_TAGS.find((x) => x === t.name)) {
          partyTags.current.set(t.id, t.name);
        }
      }
      setTags(tagsList);
    }
    if (!tagsLoaded) {
      loadTags();
      return () => {
        tagsLoaded = true;
      };
    }
  }, [tagApi]);

  const hasValidSubscription = !hasCanceledSubscription(firm);
  const queryClient = useQueryClient();

  // MUTATIONS
  // Mutation used to update the case
  const updateCaseMutation = useMutation({
    mutationFn: async (casePayload: Case) => {
      if (!casePayload.id) throw new Error("Case ID is required");
      await caseApi.casePut({
        firmId: user?.firmId || "",
        id: casePayload.id,
        _case: casePayload,
      });
    },
    onMutate: async (newCase) => {
      await queryClient.cancelQueries({
        queryKey: caseQueryOptions.queryKey,
      });
      const previousCase = queryClient.getQueryData<CaseResponse>(
        caseQueryOptions.queryKey,
      );
      queryClient.setQueryData<CaseResponse>(
        caseQueryOptions.queryKey,
        newCase as CaseResponse,
      );
      return { previousCase };
    },
    onError: (error: unknown, _newCase, context) => {
      queryClient.setQueryData(
        caseQueryOptions.queryKey,
        context?.previousCase,
      );
      setError(error instanceof Error ? error.message : "Failed to save case");
      toast.error(
        "There was an error updating the case. Please try again later.",
      );
    },
    onSuccess: () => {
      toast.success("Case has been updated.");
    },
    onSettled: async () => {
      queryClient.invalidateQueries({
        ...caseQueryOptions,
      });
      queryClient.invalidateQueries({
        ...getFirmCasesOptions(firmApi, firm?.id ?? ""),
      });
    },
  });

  // Mutation used to create a discovery request
  const createDiscoveryRequestMutation = useMutation({
    mutationFn: async ({
      createDiscoveryRequest,
    }: {
      createDiscoveryRequest: CreateDiscoveryRequest;
      case: CaseResponse;
      client?: Client;
    }) => {
      return documentApi.documentCreateDiscoveryRequest({
        createDiscoveryRequest,
      });
    },
    onSuccess: (_data, variables) => {
      const req = variables.createDiscoveryRequest;
      toast.success("Draft created.");
      history.push({
        pathname: `/clients/${variables.client?.id}/cases/${variables.case.id}/generateDiscoveryRequest`,
        search: `?documentId=${_data.id}&?type=${req.documentSubType}&partyId=${req.respondingId}&set=${req.setNumber}&start=${req.startingNumber}`,
        state: { case: variables.case, client: variables.client },
      });
    },
    onError: () => {
      toast.error(
        "Failed to create discovery request. Please try again later.",
      );
    },
  });

  const initialDraftQuestionsMutation = useMutation({
    mutationFn: async (query: ContentGetMiscContentFilteredRequest) => {
      return contentApi.contentGetMiscContentFiltered(query);
    },
    onError: () => {
      toast.error(
        "Failed to load related discovery questions. Please try again later.",
      );
    },
  });

  // Mutation used to upload files
  const uploadFilesMutation = useMutation({
    mutationFn: async ({
      files,
    }: {
      files: File[];
      newUploadingDocs: DocumentClient[];
    }) => {
      return await uploadFiles(files, id);
    },
    retry: 1,
    retryDelay: 1000,
    onError: (error) => {
      setError(error.message || "Failed to upload files");
    },
    onSettled: (_data, _error, uploadData) => {
      setUploadingDocuments([
        ...uploadingDocuments.filter(
          (doc) => !uploadData.newUploadingDocs.includes(doc),
        ),
      ]);
      queryClient.invalidateQueries({
        ...getUnarchivedCaseDocumentListOptions(documentApi, id ?? ""),
      });
    },
  });

  // Mutation used to delete (close) the case
  const deleteCaseMutation = useMutation({
    mutationFn: async ({
      deleteDocumentData,
    }: {
      deleteDocumentData: boolean;
    }) => {
      const caseId = caseQuery.data?.id ?? id ?? "";
      if (!caseId) throw new Error("Case ID is required");
      await caseApi.caseDelete({
        firmId: user?.firmId || "",
        id: caseId,
        deleteDocumentData,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: caseQueryOptions.queryKey,
      });
      await queryClient.invalidateQueries({
        queryKey: getFirmCasesOptions(firmApi, user?.firmId ?? "").queryKey,
      });
      setShowCloseCase(false);
      setDeleteDocuments(false);
      history.push("/cases");
    },
    onError: (error: unknown) => {
      setError(error instanceof Error ? error.message : "Failed to close case");
      toast.error(
        "There was an error closing the case. Please try again later.",
      );
    },
  });

  function handleChooseFile(e: React.MouseEvent) {
    e.preventDefault();
    if (!hasValidSubscription) {
      setIsSignUpModalShow(true);
      return;
    }
    fileInputRef?.current?.click();
  }

  async function handleUpload(fileList: FileList) {
    setError("");
    const files = Array.from(fileList);
    if (files.length) {
      const sanitizedFiles = sanitizeFiles(files);
      if (sanitizedFiles.length) {
        const newUploadingDocs = sanitizedFiles.map((file) =>
          getUploadingDocument(file.name),
        );
        setUploadingDocuments([...uploadingDocuments, ...newUploadingDocs]);

        uploadFilesMutation.mutate({ files: sanitizedFiles, newUploadingDocs });
      } else {
        setError("Only .pdf files are currently supported.");
      }
    }
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { files } = e.target;
    if (files) {
      handleUpload(files);
    }
    e.target.value = "";
  }

  async function saveCase(casePayload: Case) {
    await updateCaseMutation.mutate(casePayload);
    setShowEditCase(false);
  }

  function sortDocumentDates(
    first: DocumentClient,
    second: DocumentClient,
    sortColumn: string,
    sortDirectionAscending: boolean,
  ) {
    // I couldn't get realDueDate as a computed property to work, sorry
    const a = (
      (sortColumn === "realDueDate"
        ? toDateOrMin(first.info?.dueDateOverridden || first.info?.dueDate)
        : (first[sortColumn as keyof typeof first] as Date)) ??
      new Date(-8640000000000000)
    ).getTime();
    const b = (
      (sortColumn === "realDueDate"
        ? toDateOrMin(second.info?.dueDateOverridden || second.info?.dueDate)
        : (second[sortColumn as keyof typeof second] as Date)) ??
      new Date(-8640000000000000)
    ).getTime();

    return sortDirectionAscending ? a - b : b - a;
  }

  function nextDueDate(docs: DocumentClient[]) {
    const next = docs
      ?.sort((a, b) => sortDocumentDates(a, b, "realDueDate", true))
      ?.find(
        (x) =>
          toDateOrMin(x.info?.dueDateOverridden || x.info?.dueDate) >
          new Date(),
      );

    const dueDate = next?.info?.dueDateOverridden || next?.info?.dueDate;
    return dueDate ? toLocaleUSDateString(new Date(dueDate)) : "-";
  }

  function handleSelectGenerate(value: DocumentSubType): void {
    pendingDraftType.current = DocumentSubType.Unknown;
    // show info page if user has not provided info yet, else generate word document
    if (hasProvidedInfo) {
      if (!user?.isInactive && firm && isInTrialOrHasActiveSubscription(firm)) {
        setTypeToGenerate(value);
      } else {
        showSignupPage(value);
      }
    } else {
      pendingDraftType.current = value;
      setShowInfoModal(true);
    }
  }

  function showSignupPage(value: DocumentSubType) {
    rg4js("trackEvent", {
      type: "pageView",
      path: "/signup-modal",
    });
    pendingDraftType.current = value;
    setIsSignUpModalShow(true);
  }

  async function handleChosePartyAndClient(party: Party, client?: Client) {
    if (!caseQuery.data) {
      return;
    }
    setTypeToGenerate(undefined);
    const otherParties = [...(caseQuery.data.otherParties ?? [])];
    const selected = otherParties.findIndex((p) => p.id === party.id);

    if (selected === -1) {
      otherParties.push(party);
    } else {
      otherParties[selected] = party;
    }

    const caseToPush = { ...caseQuery.data, otherParties } as CaseResponse;
    const previous = nonArchivedDocs?.filter(
      (x) =>
        x.info?.otherPartyId === party.id &&
        x.info?.documentSubType === typeToGenerate,
    );

    previous?.sort(
      (a, b) => (a.info?.setNumber ?? 1) - (b.info?.setNumber ?? 1),
    );
    const highest = previous?.pop();
    const start =
      (highest?.info?.startingNumber ?? 1) +
      (highest?.interrogatories?.length ??
        highest?.info?.interrogatoryCount ??
        0);

    const _case = caseToPush;
    const partyForQuery = _case.otherParties?.find((p) => p.id === party.id);
    const query: ContentGetMiscContentFilteredRequest = {
      type: MiscContentType.DiscoveryQuestion,
      documentType: DocumentType.DiscoveryRequest,
      documentSubType: typeToGenerate ?? DocumentSubType.Unknown,
      caseType: _case.caseType ?? undefined,
      tagIds:
        !!_case.tagIds?.length || !!partyForQuery?.tagIds?.length
          ? (_case.tagIds ?? []).concat(partyForQuery?.tagIds ?? [])
          : undefined,
      isForRepresentative:
        _case.representativeType === undefined ||
        _case.representativeType === Representative.None
          ? false
          : _case.representativeType !== Representative.Individual,
      respondingParty:
        partyForQuery?.position === undefined ||
        partyForQuery.position === PartyPosition.Unknown
          ? undefined
          : convertPartyPositionToPropoundingParty(partyForQuery.position),
    };

    // Creates the initial draft questions for the discovery request
    initialDraftQuestionsMutation.mutate(query, {
      onSuccess: (data) => {
        if (!caseQuery.data?.id) return toast.error("Case ID is required");

        const questions: Question[] = data.map((m, index) => ({
          questionId: m.id,
          text: m.content,
          number: index + 1,
        }));

        const createDiscoveryRequest: CreateDiscoveryRequest = {
          caseId: caseQuery.data.id,
          clientId: client?.id,
          setNumber: (highest?.info?.setNumber ?? 0) + 1,
          startingNumber: start,
          documentSubType: typeToGenerate,
          respondingId: party.id,
          questions,
        };

        createDiscoveryRequestMutation.mutate({
          createDiscoveryRequest,
          case: caseToPush,
          client: client,
        });
      },
    });
  }

  function onCaseCloseClose() {
    setShowCloseCase(false);
    setDeleteDocuments(false);
  }

  function refetchDocumentList() {
    const caseDocumentListQueryKey = getUnarchivedCaseDocumentListOptions(
      documentApi,
      id ?? "",
    ).queryKey;

    queryClient.invalidateQueries({ queryKey: caseDocumentListQueryKey });
    queryClient.refetchQueries({ queryKey: caseDocumentListQueryKey });
    queryClient.invalidateQueries(getProcessingDocumentsOptions(documentApi));
    queryClient.refetchQueries(getProcessingDocumentsOptions(documentApi));
  }

  function externalUploadRefresh() {
    setShowUploadDocs(false);
    refetchDocumentList();
  }

  function getCaseSourceLogo(partner?: ExternalPartners) {
    if (!partner || partner === ExternalPartners.None) {
      return <BriefpointLogo title="Briefpoint Case" />;
    }

    switch (partner) {
      case ExternalPartners.Clio:
        return <ClioLogo title="Clio Case" />;
      case ExternalPartners.Smokeball:
        return <SmokeballLogo title="Smokeball Case" />;
      case ExternalPartners.MyCase:
        return <MyCaseLogo title="MyCase Case" />;
      default:
        return <BriefpointLogo title="Briefpoint Case" />;
    }
  }

  function setSelectedTab(tab: CaseTab) {
    history.push({
      search: `?tab=${tab}`,
    });
  }

  if (caseQuery.isError) {
    return (
      <Container className="page-container-multi">
        <Row style={{ marginTop: "24px" }}>
          <Col>
            <p>Unable to load this case.</p>
            <Link to="/cases">Back to cases</Link>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <>
      <LeavingGuard
        when={uploadingDocuments.length > 0}
        message="Your upload will stop if you leave this page."
        title="Upload in progress!"
      />
      <Container className="page-container-multi">
        <Loading
          isLoading={
            documentsQuery.isLoading ||
            (caseQuery.isPending && !caseQuery.data) ||
            (clientsQuery.isPending && !clientsQuery.data) ||
            users === undefined
          }
        >
          <Row key="heading" style={{ marginTop: "24px" }}>
            <Col>
              <div className="bread-crumbs mb-4">
                <Link to={"/cases"}>Cases</Link>{" "}
                {`> ${caseQuery.data?.shortTitle}`}
              </div>
            </Col>
            <Col className="actions-cell" />
          </Row>
          <Row
            key="class-details-wrapper-multi"
            className="class-details-wrapper-multi"
          >
            <Row className="case-details-title-wrapper">
              <Col className="case-details-title">
                {!!firm?.preferences?.enabledExternalProviders?.size && (
                  <span className="sourceLogo">
                    {getCaseSourceLogo(caseQuery.data?.integration?.partner)}
                  </span>
                )}
                {caseQuery.data?.shortTitle || caseQuery.data?.title}
              </Col>
            </Row>
            <Row className="case-details">
              <Col className="col-9">
                <Row key="case-headers-main" className="case-headers">
                  <Col className="col-2">Case Type</Col>
                  <Col className="col-3">Tags</Col>
                  <Col>Case Number</Col>
                  <Col>Venue</Col>
                  <Col>Lead Attorney</Col>
                  {!!jurisdiction?.supportsDueDates && <Col>Next Due Date</Col>}
                </Row>
                <Row key="case-details-main">
                  <Col className="col-2">
                    {caseTypeDisplay(
                      caseQuery.data?.caseType,
                      jurisdiction,
                      true,
                      "-",
                    )}
                  </Col>
                  <Col className="col-3">
                    <TagsList
                      tagIds={caseQuery.data?.tagIds}
                      availableTags={tags}
                      questionId={caseQuery.data?.id?.toString() ?? ""}
                      startTrimmingTagsAfter={3}
                    />
                  </Col>
                  <Col>{caseQuery.data?.caseNumber ?? "-"}</Col>
                  <Col title={venue?.shortName ?? ""}>{venue?.shortName}</Col>
                  <Col>{users && leadAttorneyName(caseQuery.data, users)}</Col>
                  {!!jurisdiction?.supportsDueDates && (
                    <Col>{nextDueDate(documentsQuery.data ?? [])}</Col>
                  )}
                </Row>
              </Col>
              <Col className="actions-cell col-3">
                <div className="case-actions-col">
                  <Button
                    data-testid="close-case-button"
                    variant="link"
                    style={{ marginRight: "8px" }}
                    className="close-case-btn"
                    title="Close Case"
                    onClick={() => setShowCloseCase(true)}
                  >
                    Close Case
                  </Button>
                  <Button
                    variant="outline"
                    className="btn-26 edit-case-btn"
                    onClick={() => setShowEditCase(true)}
                  >
                    Edit Case
                  </Button>
                </div>
              </Col>
            </Row>
          </Row>
          {selectedTab === CASE_TABS.DISCOVERY && (
            <Row key="actions" className="table-actions-row">
              {caseQuery.data?.integration?.identifier && (
                <Col className="col-auto">
                  <ExternalPartnerConnection />
                </Col>
              )}

              <Col className="col-auto">
                <Dropdown>
                  <Dropdown.Toggle
                    as={Button}
                    variant="outline"
                    size="sm"
                    id="draft-dropdown"
                  >
                    Draft Request (Beta)
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      onClick={() =>
                        handleSelectGenerate(
                          DocumentSubType.RequestsForAdmission,
                        )
                      }
                    >
                      {
                        getSubTypeText(
                          DocumentSubType.RequestsForAdmission,
                          jurisdiction?.id ?? "",
                        ).singular
                      }
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() =>
                        handleSelectGenerate(
                          DocumentSubType.RequestsForProduction,
                        )
                      }
                    >
                      {
                        getSubTypeText(
                          DocumentSubType.RequestsForProduction,
                          jurisdiction?.id ?? "",
                        ).singular
                      }
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() =>
                        handleSelectGenerate(
                          DocumentSubType.SpecialInterrogatories,
                        )
                      }
                    >
                      {
                        getSubTypeText(
                          DocumentSubType.SpecialInterrogatories,
                          jurisdiction?.id ?? "",
                        ).plural
                      }
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
              <Col className="d-flex col-auto gap-2">
                {user?.externalConnection?.isActive ? (
                  <ExternalPartnerMenu
                    _case={caseQuery.data}
                    externalAction={setShowUploadDocs}
                    bpDefault={handleChooseFile}
                    isUploading={uploadFilesMutation.isPending}
                  />
                ) : (
                  <Button
                    data-testid="upload-document-button"
                    variant="default"
                    size="sm"
                    onClick={handleChooseFile}
                    disabled={uploadFilesMutation.isPending}
                  >
                    {uploadFilesMutation.isPending ? "Uploading..." : "Respond"}
                  </Button>
                )}
                <Help
                  id="document-help"
                  text={`Briefpoint currently accepts the following state court documents: <ul><li class="tooltip-row">${getSubTypeText(DocumentSubType.RequestsForAdmission, getCaseJurisdiction(casesQuery.data, caseQuery.data?.id ?? "") ?? "").plural}</li><li class="tooltip-row">${getSubTypeText(DocumentSubType.RequestsForProduction, getCaseJurisdiction(casesQuery.data, caseQuery.data?.id ?? "") ?? "").plural}</li><li class="tooltip-row">${getSubTypeText(DocumentSubType.SpecialInterrogatories, getCaseJurisdiction(casesQuery.data, caseQuery.data?.id ?? "") ?? "").plural}</li>${getCaseJurisdiction(casesQuery.data, caseQuery.data?.id ?? "") === CaliforniaId ? `<li class="tooltip-row">${getSubTypeText(DocumentSubType.FormInterrogatories, getCaseJurisdiction(casesQuery.data, caseQuery.data?.id ?? "") ?? "").plural}</li>` : ""}</ul>`}
                  direction={"bottom"}
                />
              </Col>

              <Col className="col-auto" />
            </Row>
          )}
          {userCanUseDocHandling && (
            <CaseTabs
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
            />
          )}
          {selectedTab === CASE_TABS.PRODUCTION && caseQuery.data && (
            <CaseProduction case={caseQuery.data} />
          )}
          {selectedTab === CASE_TABS.DISCOVERY && (
            <CaseDiscovery
              id={id}
              jurisdiction={jurisdiction}
              _case={caseQuery.data}
              clients={clientsQuery.data}
              chooseFile={handleChooseFile}
              isUploading={uploadFilesMutation.isPending}
            />
          )}
          {showEditCase && caseQuery.data && firm && (
            <AddEditCaseModal
              availableTags={tags}
              setAvailableTags={setTags}
              title="Edit Case"
              confirm="Save"
              isShow
              onClose={() => setShowEditCase(false)}
              onConfirm={saveCase}
              users={users}
              jurisdictions={jurisdictions ?? []}
              _case={caseQuery.data}
            />
          )}
          {caseQuery.data && jurisdictions && missingRequiredInfo && (
            <RequiredCaseInfo
              key={requiredCaseInfoKey}
              show={!!typeToGenerate && missingRequiredInfo}
              onClose={() => setTypeToGenerate(undefined)}
              onConfirm={(c) => updateCaseMutation.mutateAsync(c)}
              savePending={updateCaseMutation.isPending}
              _case={caseQuery.data}
              jurisdictions={jurisdictions}
            />
          )}
          {caseQuery.data && tags && jurisdiction && (
            <SelectPartyModal
              availableTags={filterTagsForParties(
                tags,
                caseQuery.data,
                partyTags.current,
              )}
              show={!!typeToGenerate && !missingRequiredInfo}
              type={typeToGenerate}
              onClose={() => setTypeToGenerate(undefined)}
              onConfirm={handleChosePartyAndClient}
              jurisdiction={jurisdiction}
              _case={caseQuery.data}
              clients={clientsQuery.data}
            />
          )}
          <SignUpModal
            isShow={isSignUpModalShow}
            onClose={(success) => {
              setIsSignUpModalShow(false);
              if (success && pendingDraftType.current) {
                handleSelectGenerate(pendingDraftType.current);
              }
            }}
          />
          {!hasProvidedInfo && (
            <Modal
              show={showInfoModal}
              onClose={() => setShowInfoModal(false)}
              size="xl"
              title={"Information for output document"}
              showCancelButton={false}
              showConfirmButton={false}
              preventBackgroundClick
            >
              <GeneralInformationScreen
                documentId={"anything"}
                onSave={async () => {
                  setShowInfoModal(false);
                  if (pendingDraftType.current) {
                    handleSelectGenerate(pendingDraftType.current);
                  }
                }}
              />
            </Modal>
          )}
        </Loading>
      </Container>
      <BPModal
        onClose={onCaseCloseClose}
        onConfirm={() =>
          deleteCaseMutation.mutate({ deleteDocumentData: deleteDocuments })
        }
        show={showCloseCase}
        title={<span className="fw-500">Close Case</span>}
        confirmText={`Close Case${deleteDocuments ? " and Delete Documents" : ""}`}
        cancelText={
          <Button variant="outline" onClick={onCaseCloseClose}>
            Cancel
          </Button>
        }
        confirming={deleteCaseMutation.isPending}
        confirmingText="Closing..."
      >
        <div>
          <div className="modal-body-text mb-3">
            Please confirm you wish to close this Case, it will no longer show
            up in search results on your dashboard.
          </div>
          <Form.Check
            data-testid="delete-docs-checkbox"
            id="delete-docs"
            name="delete-docs"
            checked={deleteDocuments}
            onChange={(e) => setDeleteDocuments(e.target.checked)}
            label="Also delete all associated Documents."
            className="modal-body-text-checkbox"
          />
          {deleteDocuments && (
            <div className="modal-body-text mt-3 mb-3 text-danger">
              This will delete all Documents associated with this Case along
              with any content entered for those Documents: objections,
              responses, etc. <strong>This action cannot be undone.</strong>
            </div>
          )}
        </div>
      </BPModal>
      {caseQuery.data?.integration?.identifier && (
        <Modal
          onClose={() => setShowUploadDocs(false)}
          className="externalModal"
          show={showUploadDocs}
          title="Upload Files"
          confirmText="Upload Files"
        >
          <ExternalFileBrowserSelect
            case={caseQuery.data}
            setIsExternalUploading={setIsExternalUploading}
            isExternalUploading={isExternalUploading}
            onFinish={externalUploadRefresh}
            onUploadSuccess={refetchDocumentList}
          />
        </Modal>
      )}

      <input
        data-testid="document-file-input"
        type="file"
        id="file-input"
        name="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="d-none"
        multiple
      />
    </>
  );
}
